import { AudienceIntelligence, Brand, BrandIntelligence, CompetitiveIntel, CreativeSystem, MarketIntelligence, SocialAndDigital } from "@/schema/brand-intelligence";

const brand: Brand = {
  name: "Curapod by LiteMed",
  industry: ["Medical Equipment Manufacturing", "Manufacture of Medical, Precision and Optical Instruments"],
  sub_industry: ["Therapeutic Devices", "Non-invasive pod for pain management", "Photobiomodulation (Light Therapy) Devices"],
  founded_year: 2022,
  founded_original_name: "LiteMed India Private Limited",
  headquarters: ["Hyderabad, India"],
  tagline_primary: "Empowering lives with cutting-edge solutions.",
  mission: '"To empower lives with cutting-edge solutions, Litemed pioneers innovative health technologies for pain-free relief."',
  vision: '"Litemed envisions a world where people embrace life without fear of chronic pain."',
  overview:
    "Litemed is a product company innovating light therapy devices for pain management. Founded in 2022 in Hyderabad, India, it offers non-invasive treatments. The flagship Curapod uses photobiomodulation technology for drug-free pain relief and cellular-level healing globally.",
  scale: {
    revenue: 0.2,
    employees_estimate: 23,
    markets_served_count: 3,
    operating_countries_count: 1,
  },
  global_presence: {
    regions: [
      {
        region: "India",
        headquarters: "Hyderabad, India",
        active_markets: ["India"],
        market_type: "Direct (D2C sales through company website and select retail partners)",
      },
    ],
    key_markets_by_revenue_share: [
      {
        region: "India",
        share_percent: 100,
        notes: "Approximately 100% of current revenue (primary and only active market as of early 2025)",
      },
    ],
  },
};

const creativeSystem: CreativeSystem = {
  visual_identity: {
    colors: {
      primary: [
        {
          name: "LiteMed Black",
          hex: "#000000",
        },
        {
          name: "Medical White",
          hex: "#FFFFFF",
        },
        {
          name: "Deep Charcoal",
          hex: "#1A1A1A",
        },
      ],
      secondary: [
        {
          name: "Clinical Gray",
          hex: "#6B6B6B",
        },
        {
          name: "Light Gray",
          hex: "#F5F5F5",
        },
        {
          name: "Soft Neutral",
          hex: "#E8E8E8",
        },
      ],
      accent: [
        {
          name: "Therapeutic Red",
          hex: "#DC143C",
        },
        {
          name: "Near-Infrared Crimson",
          hex: "#8B0000",
        },
        {
          name: "Healing Amber",
          hex: "#FF6B35",
        },
      ],
    },
    typography: [
      {
        font_family: "Sans-serif (system default)",
        weight: "Regular (400), Medium (500)",
        usage: "Body text, product descriptions, general content",
      },
      {
        font_family: "Sans-serif (system default, likely Helvetica/Arial fallback)",
        weight: "Bold (700), Semi-Bold (600)",
        usage: "Headlines, section titles, CTAs",
      },
      {
        font_family: "Sans-serif (clean, modern)",
        weight: "Medium (500)",
        usage: "Navigation, buttons, UI elements",
      },
    ],
    logos: [
      {
        variant: "Primary Black Logo",
        url: "https://assets.zocket.com/uploads/0d611d79-dfd8-4a25-97a2-74903038e3e0-images.png",
      },
    ],
  },
  verbal_identity: {
    voice_traits: ["Empowering", "Compassionate", "Scientific/Clinical", "Hopeful", "Innovative", "Accessible", "Trustworthy"],
    taglines: [
      "Empowering lives with cutting-edge solutions",
      "Adaptive pain management for real life",
      "Curapod: Your partner in achieving a pain-free, active life",
      "That's the promise of Litemed – a beacon of hope in the darkest of times",
      "At the end of every dark tunnel, there is light",
      "World's First Adaptive Pain Management System",
    ],
    messaging_dos: [],
    messaging_donts: [],
    banned_words: ["cure", "miracle", "guaranteed results"],
  },
  creative_assets: [
    "https://assets.zocket.com/uploads/e257f438-b91e-4709-b3b1-6b2f27330b5c-image.png",
    "https://assets.zocket.com/uploads/2420a233-89e1-4bcd-8f55-6bce257a85e8-Screenshot_2026-02-06_152428.png",
    "https://assets.zocket.com/uploads/fc9a9cfd-49b1-4ffd-93fa-c6150956ef33-image_1_.png",
  ],
  compliance: {
    brand_safety_guidelines: [],
    copyright_trademark_usage: [],
  },
  ai_creative_memory: {
    design_memory: {
      pattern_accuracy: "87%",
      patterns_learned: 156,
      avg_generation: "1.8s",
    },
    style_drift_alerts: [
      {
        title: "Curapod Product Logo",
        description: "Logo placement inconsistent with brand guidelines",
        severity: "medium",
      },
      {
        title: "Instagram Profile Logo",
        description: "Logo placement inconsistent with brand guidelines",
        severity: "medium",
      },
    ],
    preference_learning: [
      { preference: "Clean Medical", score: 91, change: 5 },
      { preference: "Trust & Care", score: 88, change: 4 },
      { preference: "Minimal Clinical", score: 85, change: 2 },
      { preference: "Wellness Focus", score: 81, change: 6 },
    ],
  },
  brand_architecture: {
    available_brands: ["LiteMed", "Curapod"],
    selected_brand: "LiteMed / Curapod",
  },
};

const marketIntelligence: MarketIntelligence = {
  kpis: {
    sentiment: 7.2,
    sentiment_graph: [
      {
        metric: "Customer Sentiment Score",
        value: "72%",
        delta: "+8.5%",
      },
      {
        metric: "Market Penetration (India)",
        value: "<1%",
        delta: "+0.3%",
      },
      {
        metric: "Revenue Growth (MoM)",
        value: "20%",
        delta: "+20%",
      },
      {
        metric: "Brand Awareness (SOV)",
        value: "~2-3%",
        delta: "+1.2%",
      },
      {
        metric: "Clinical Efficacy Rating",
        value: "85%",
        delta: "+5%",
      },
      {
        metric: "User Base Growth",
        value: "+150%",
        delta: "+150%",
      },
      {
        metric: "Competitive Ranking",
        value: "#6/83",
        delta: "+2 positions",
      },
      {
        metric: "Funding Momentum",
        value: "$2.43M",
        delta: "+$0.93M",
      },
    ],
  },
  sentiment_summary: {
    time_range: ["January", "February"],
    themes: {
      positive: ["Effective Pain Relief", "Portability & Convenience", "Drug-Free Alternative", "Clinical Validation", "Ease of Use", "Excellent Customer Support", "Made in India Innovation"],
      neutral: ["Price Point Consideration", "Learning Curve", "Treatment Duration", "Market Awareness", "Charging Frequency"],
      negative: [
        "App User Experience Issues",
        "Device Pairing Problems",
        "Power Button Reliability",
        "Lack of Physical Controls",
        "Multi-User Limitations",
        "Session End Notification",
        "App Stability",
      ],
    },
  },
  distribution: [
    {
      brand: "TENS Device Manufacturers",
      share: 45,
    },
    {
      brand: "Neurostimulation Devices",
      share: 28,
    },
    {
      brand: "Topical Pain Relief Devices",
      share: 15,
    },
    {
      brand: "Emerging Photobiomodulation",
      share: 7,
    },
    {
      brand: "Others",
      share: 5,
    },
  ],
  sentiment_by_product: [
    {
      product: "Curapod (Core Device)",
      sentiment: 78,
      change_delta: 12,
    },
    {
      product: "Curapod for Knee Pain",
      sentiment: 82,
      change_delta: 15,
    },
    {
      product: "Curapod for Back Pain",
      sentiment: 76,
      change_delta: 10,
    },
    {
      product: "Curapod for Fibromyalgia",
      sentiment: 74,
      change_delta: 8,
    },
    {
      product: "Curapod for Sports Injuries",
      sentiment: 80,
      change_delta: 14,
    },
    {
      product: "Curapod Mobile App",
      sentiment: 52,
      change_delta: -5,
    },
    {
      product: "Curapod Accessories",
      sentiment: 71,
      change_delta: 3,
    },
    {
      product: "Customer Support Service",
      sentiment: 88,
      change_delta: 18,
    },
  ],
  top_topics: {
    praise: [
      {
        topic: "Significant Pain Reduction",
        weight: 10,
      },
      {
        topic: "Portability & Discreet Use",
        weight: 9,
      },
      {
        topic: "Drug-Free Solution",
        weight: 9,
      },
      {
        topic: "Fast-Acting Relief",
        weight: 8,
      },
      {
        topic: "Exceptional Customer Service",
        weight: 8,
      },
      {
        topic: "Clinical Credibility",
        weight: 7,
      },
      {
        topic: "Made in India Innovation",
        weight: 7,
      },
    ],
    complaints: [
      {
        topic: "Poor App User Experience",
        weight: 10,
      },
      {
        topic: "Device Pairing/Reset Issues",
        weight: 8,
      },
      {
        topic: "Power Button Unreliability",
        weight: 7,
      },
      {
        topic: "Lack of Physical Off Switch",
        weight: 6,
      },
      {
        topic: "Frequent Charging Required",
        weight: 5,
      },
      {
        topic: "No Multi-User Profiles",
        weight: 3,
      },
      {
        topic: "Missing Session End Alert",
        weight: 2,
      },
    ],
  },
};

const competitiveIntel: CompetitiveIntel = {
  primary_competitors: [
    {
      name: "Theranica Bio-Electronics Ltd.",
      threat_level: "high",
      market_share: 4,
      sentiment: 78,
    },
    {
      name: "Omron Healthcare, Inc.",
      threat_level: "high",
      market_share: 10,
      sentiment: 82,
    },
    {
      name: "Medtronic Plc",
      threat_level: "medium",
      market_share: 20,
      sentiment: 72,
    },
    {
      name: "Boston Scientific Corporation",
      threat_level: "medium",
      market_share: 16,
      sentiment: 70,
    },
    {
      name: "Abbott Laboratories",
      threat_level: "medium",
      market_share: 14,
      sentiment: 68,
    },
    {
      name: "Nevro Corp.",
      threat_level: "medium",
      market_share: 11,
      sentiment: 65,
    },
    {
      name: "Koninklijke Philips N.V.",
      threat_level: "medium-low",
      market_share: 7,
      sentiment: 75,
    },
    {
      name: "Beurer GmbH",
      threat_level: "high",
      market_share: 6,
      sentiment: 80,
    },
  ],
  secondary_competitors: [
    {
      name: "LumiWave Medical",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "LightForce Therapy Lasers",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "BioPhotas, Inc.",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "Joovv Inc.",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "Zynex, Inc.",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "Stimwave LLC",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
  ],
  share_of_voice: [
    {
      channel: "Digital/Online Advertising",
      distribution: {
        "Omron Healthcare": 16,
        Medtronic: 14,
        "Boston Scientific": 11,
        Theranica: 9,
        Others: 50,
      },
    },
    {
      channel: "Social Media Presence",
      distribution: {
        "Omron Healthcare": 14,
        Medtronic: 11,
        "Boston Scientific": 23,
        Theranica: 7,
        Others: 45,
      },
    },
    {
      channel: "Medical/Professional Publications",
      distribution: {
        "Omron Healthcare": 28,
        Medtronic: 28,
        "Boston Scientific": 21,
        Theranica: 17,
        Others: 23,
      },
    },
    {
      channel: "Retail/E-commerce Visibility",
      distribution: {
        "Omron Healthcare": 28,
        Medtronic: 23,
        "Boston Scientific": 17,
        Theranica: 17,
        Others: 23,
      },
    },
  ],
  competitive_benchmarking: [
    {
      metric_name: "Brand Awareness Score 2025",
      brands: [
        {
          brand_name: "Omron Healthcare",
          score: 85,
        },
        {
          brand_name: "Medtronic",
          score: 88,
        },
        {
          brand_name: "Boston Scientific",
          score: 82,
        },
        {
          brand_name: "Theranica",
          score: 45,
        },
        {
          brand_name: "LiteMed",
          score: 69,
        },
      ],
    },
    {
      metric_name: "Customer Satisfaction Index 2025",
      brands: [
        {
          brand_name: "Omron Healthcare",
          score: 82,
        },
        {
          brand_name: "Theranica",
          score: 78,
        },
        {
          brand_name: "LiteMed",
          score: 75,
        },
        {
          brand_name: "Medtronic",
          score: 72,
        },
        {
          brand_name: "Boston Scientific",
          score: 70,
        },
      ],
    },
    {
      metric_name: "Net Promoter Score 2025 (normalized)",
      brands: [
        {
          brand_name: "Theranica",
          score: 71,
        },
        {
          brand_name: "Omron Healthcare",
          score: 69,
        },
        {
          brand_name: "LiteMed",
          score: 65,
        },
        {
          brand_name: "Medtronic",
          score: 61,
        },
        {
          brand_name: "Boston Scientific",
          score: 60,
        },
      ],
    },
    {
      metric_name: "Product Reliability & Safety Score 2025",
      brands: [
        {
          brand_name: "Omron Healthcare",
          score: 88,
        },
        {
          brand_name: "Theranica",
          score: 82,
        },
        {
          brand_name: "LiteMed",
          score: 78,
        },
        {
          brand_name: "Medtronic",
          score: 70,
        },
        {
          brand_name: "Boston Scientific",
          score: 68,
        },
      ],
    },
    {
      metric_name: "Technology Innovation Index 2025",
      brands: [
        {
          brand_name: "Theranica",
          score: 90,
        },
        {
          brand_name: "Boston Scientific",
          score: 88,
        },
        {
          brand_name: "LiteMed",
          score: 85,
        },
        {
          brand_name: "Medtronic",
          score: 82,
        },
        {
          brand_name: "Omron Healthcare",
          score: 60,
        },
      ],
    },
    {
      metric_name: "Consumer Accessibility & Affordability 2025",
      brands: [
        {
          brand_name: "Omron Healthcare",
          score: 95,
        },
        {
          brand_name: "LiteMed",
          score: 70,
        },
        {
          brand_name: "Theranica",
          score: 55,
        },
        {
          brand_name: "Medtronic",
          score: 20,
        },
        {
          brand_name: "Boston Scientific",
          score: 20,
        },
      ],
    },
  ],
  positioning: {
    competitive_advantages: [
      "High innovation score with adaptive photobiomodulation technology",
      "Affordable pricing compared to surgical alternatives",
      "Early traction with 10K Instagram followers and 20% MoM revenue growth",
      "Targeting 30+ musculoskeletal conditions with portable, hands-free design",
      "Addressing India's chronic pain market",
    ],
    improvement_areas: [
      "Very low brand awareness compared to established competitors",
      "Limited distribution with D2C only",
      "Small social footprint compared to competitors",
      "Market share less than 1%",
      "Limited long-term clinical validation",
    ],
    disruption_watchlist: ["Hinge Health", "Matri", "NeuraLace Medical", "Tivic Health", "AposHealth"],
  },
};

const audienceIntelligence: AudienceIntelligence = {
  audience_summary:
    "LiteMed's target audience primarily consists of individuals aged 45-65 years, with a significant prevalence of chronic pain conditions. The audience is predominantly female, with a median income range representing India's middle class. They are health-conscious, technology-adopters, and value-conscious, seeking non-invasive, drug-free solutions for pain management. The audience is also characterized by a strong interest in health and wellness, medical technology, and preventive healthcare.",
  core_demographics: {
    primary_age_range: "45-65 years",
    gender_split: {
      male: 37.5,
      female: 62.5,
    },
    income_median_usd: 6800,
    location_type: "Urban",
    demographics_distribution: {
      age: [
        { age_range: "45-54", percentage: 25 },
        { age_range: "55-64", percentage: 25 },
        { age_range: "65+", percentage: 50 },
      ],
      education: [
        { education_level: "Bachelor's+", percentage: 48 },
        { education_level: "Some College", percentage: 28 },
        { education_level: "Graduate Degree", percentage: 18 },
        { education_level: "High School", percentage: 6 },
      ],
      geographic: [
        { location: "Urban", percentage: 50 },
        { location: "Suburban", percentage: 30 },
        { location: "Rural", percentage: 20 },
      ],
    },
  },
  occupation_distribution: [
    { occupation: "Healthcare Professionals", percentage: 35 },
    { occupation: "Healthcare Administrators", percentage: 22 },
    { occupation: "Healthcare Support Staff", percentage: 15 },
    { occupation: "Healthcare Educators", percentage: 12 },
    { occupation: "Healthcare Researchers", percentage: 16 },
  ],
  interests: {
    top_categories: [
      {
        category: "Health & Wellness",
        score: 9,
      },
      {
        category: "Pain Management Solutions",
        score: 10,
      },
      {
        category: "Medical Technology",
        score: 8,
      },
      {
        category: "Fitness & Physical Activity",
        score: 7,
      },
      {
        category: "Alternative Medicine/Ayurveda",
        score: 7,
      },
      {
        category: "Preventive Healthcare",
        score: 8,
      },
      {
        category: "Digital Health Apps",
        score: 7,
      },
      {
        category: "Physiotherapy & Rehabilitation",
        score: 8,
      },
      {
        category: "Chronic Disease Management",
        score: 8,
      },
      {
        category: "Home Healthcare",
        score: 8,
      },
    ],
    brand_affinities: [
      {
        brand: "Omron",
        affinity: 35,
      },
      {
        brand: "Philips Healthcare",
        affinity: 32,
      },
      {
        brand: "1mg/Tata Digital Health",
        affinity: 40,
      },
      {
        brand: "Practo",
        affinity: 38,
      },
      {
        brand: "Netmeds/Apollo Pharmacy",
        affinity: 35,
      },
      {
        brand: "Fitbit/Xiaomi",
        affinity: 28,
      },
      {
        brand: "Ayurvedic Brands (Himalaya, Patanjali)",
        affinity: 42,
      },
      {
        brand: "Medtronic/Abbott",
        affinity: 25,
      },
      {
        brand: "Amazon Health/Diagnostics",
        affinity: 30,
      },
      {
        brand: "Local Physiotherapy Clinics",
        affinity: 33,
      },
    ],
  },
  behavioral_insights: {
    shopping_behavior: {
      purchase_channel_distribution: {
        "Online Direct (Brand Website)": 42,
        "E-commerce Platforms (Amazon, Flipkart)": 28,
        "Healthcare E-commerce (1mg, Netmeds)": 15,
        "Retail Pharmacy/Medical Stores": 10,
        "Hospital/Clinic Recommendations": 5,
      },
      purchase_frequency: "0.8-1.2x per year",
      avg_order_value_usd: 110,
    },
    decision_journey: {
      stages: [
        {
          stage: 1,
          name: "Pain Point Identification",
          description: "Chronic pain impacts daily life; seeking alternatives to medication",
          duration_days: 45,
        },
        {
          stage: 2,
          name: "Solution Discovery",
          description: "Online research on photobiomodulation, reading reviews, comparing devices, consulting doctors",
          duration_days: 18,
        },
        {
          stage: 3,
          name: "Brand Comparison",
          description: "Evaluating Curapod features, clinical validation, price, warranty, customer testimonials",
          duration_days: 10,
        },
        {
          stage: 4,
          name: "Transaction",
          description: "Selecting purchase channel, payment method (EMI options important), placing order",
          duration_days: 2,
        },
        {
          stage: 5,
          name: "Usage & Recommendation",
          description: "Using device, tracking results via app, sharing experience, potential referrals",
          duration_days: 90,
        },
      ],
    },
    media_and_device_usage: {
      device_preference: {
        "Mobile (Smartphone)": 62,
        "Desktop/Laptop": 28,
        Tablet: 10,
      },
      media_channels: {
        YouTube: 28,
        "Google Search": 25,
        Facebook: 18,
        Instagram: 15,
        WhatsApp: 10,
        LinkedIn: 4,
      },
    },
  },
  platforms: {
    distribution: {
      "Online Direct (Brand Website)": 42,
      "E-commerce Platforms (Amazon, Flipkart)": 28,
      "Healthcare E-commerce (1mg, Netmeds)": 15,
      "Retail Pharmacy/Medical Stores": 10,
      "Hospital/Clinic Recommendations": 5,
    },
  },
};

const socialAndDigital: SocialAndDigital = {
  platform_metrics: [
    {
      platform: "Instagram",
      followers: 10000,
      health_score: 75,
    },
    {
      platform: "LinkedIn",
      followers: 1527,
      health_score: 85,
    },
    {
      platform: "YouTube",
      followers: 0,
      health_score: 0,
    },
    {
      platform: "Facebook",
      followers: 0,
      health_score: 0,
    },
    {
      platform: "Twitter/X",
      followers: 0,
      health_score: 0,
    },
    {
      platform: "TikTok",
      followers: 0,
      health_score: 0,
    },
    {
      platform: "iOS App Store & Google Play Store",
      followers: 0,
      health_score: 50,
    },
    {
      platform: "litemed.co.in",
      followers: 0,
      health_score: 85,
    },
  ],
  sentiment_distribution: [
    {
      label: "Positive",
      percentage: 40,
    },
    {
      label: "Neutral",
      percentage: 40,
    },
    {
      label: "Negative",
      percentage: 20,
    },
  ],
  recent_comments: [],
  engagement: {
    engagement_by_platform: [
      {
        platform: "Instagram",
        engagement_rate: 3,
      },
      {
        platform: "LinkedIn",
        engagement_rate: 2.5,
      },
      {
        platform: "YouTube",
        engagement_rate: 0,
      },
      {
        platform: "Facebook",
        engagement_rate: 0,
      },
      {
        platform: "Twitter/X",
        engagement_rate: 0,
      },
      {
        platform: "TikTok",
        engagement_rate: 0,
      },
    ],
    best_performing_content_types: [
      {
        content_type: "Instagram Carousels",
        performance: 9,
      },
      {
        content_type: "Educational/Explainer Content",
        performance: 8,
      },
      {
        content_type: "Customer Testimonials & Success Stories",
        performance: 8,
      },
      {
        content_type: "Instagram Reels",
        performance: 7,
      },
      {
        content_type: "LinkedIn Photo/Video Posts",
        performance: 8,
      },
      {
        content_type: "Facebook Albums",
        performance: 7,
      },
      {
        content_type: "Blog/Long-form Content",
        performance: 6,
      },
    ],
    follower_growth_trend: [
      {
        month: "September",
        followers: 9527,
      },
      {
        month: "October",
        followers: 5527,
      },
      {
        month: "November",
        followers: 7527,
      },
      {
        month: "December",
        followers: 12747,
      },
      {
        month: "January",
        followers: 16902,
      },
      {
        month: "February",
        followers: 17327,
      },
    ],
  },
};

export const mBrandIntelligenceLitemed: BrandIntelligence = {
  brand,
  creative_system: creativeSystem,
  market_intelligence: marketIntelligence,
  competitive_intel: competitiveIntel,
  audience_intelligence: audienceIntelligence,
  social_and_digital: socialAndDigital,
};
